import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

const PayableAgingReport = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [groupedData, setGroupedData] = useState({});
    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState("");
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [isApplied, setIsApplied] = useState(false);
    const [summaryData, setSummaryData] = useState({
        totalOutstanding: 0,
        agingBuckets: {
            "0-30": 0,
            "31-60": 0,
            "61-90": 0,
            "90+": 0,
        },
    });

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const userInfo = JSON.parse(localStorage.getItem("user-info"));
    const origin = localStorage.getItem("user-origin");
    const token = userInfo?.token;

    // Initial load for vendors only
    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            // Fetch vendors for the dropdown
            const response = await axios.get(`${backendUrl}/api/contacts?type=vendor`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                },
            });
            const vendorList = response.data.Data || [];
            setVendors(vendorList.map(v => ({ id: v.id, name: v.name })));
        } catch (error) {
            console.error("Error loading vendors:", error);
        }
    };

    // Live search within current results
    useEffect(() => {
        let result = data;

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(
                (item) =>
                    item.vendor_name?.toLowerCase().includes(term) ||
                    item.invoice_no?.toLowerCase().includes(term) ||
                    item.expense_type_name?.toLowerCase().includes(term)
            );
        }

        setFilteredData(result);
        groupByVendor(result);
        calculateSummary(result);
    }, [searchTerm, data]);

    const handleApplyFilters = async () => {
        try {
            setLoading(true);
            setIsApplied(true);
            const params = {
                vendor_id: selectedVendor || undefined,
                from_date: fromDate ? fromDate.format("YYYY-MM-DD") : undefined,
                to_date: toDate ? toDate.format("YYYY-MM-DD") : undefined,
            };

            const response = await axios.get(`${backendUrl}/api/payable`, {
                params,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                },
            });

            const reportData = response.data.data || [];
            setData(reportData);
        } catch (error) {
            console.error("Error loading payable aging report:", error);
        } finally {
            setLoading(false);
        }
    };

    const calculateSummary = (items) => {
        let total = 0;
        const buckets = {
            "0-30": 0,
            "31-60": 0,
            "61-90": 0,
            "90+": 0,
        };

        items.forEach((item) => {
            const amount = parseFloat(item.outstanding.replace(/,/g, ""));
            total += amount;

            if (item.aging_bucket === "0–30 Days") buckets["0-30"] += amount;
            else if (item.aging_bucket === "31–60 Days") buckets["31-60"] += amount;
            else if (item.aging_bucket === "61–90 Days") buckets["61-90"] += amount;
            else buckets["90+"] += amount;
        });

        setSummaryData({
            totalOutstanding: total,
            agingBuckets: buckets,
        });
    };

    const groupByVendor = (items) => {
        const grouped = {};
        items.forEach((item) => {
            if (!grouped[item.vendor_name]) grouped[item.vendor_name] = [];
            grouped[item.vendor_name].push(item);
        });
        setGroupedData(grouped);
    };

    const exportToExcel = () => {
        const flatData = filteredData.map((item) => ({
            "Vendor Name": item.vendor_name,
            "Reference/Invoice #": item.invoice_no,
            "Expense Date": item.date,
            "Due Date (30d)": item.due_date,
            "Bill Amount": item.amount,
            "Currency": item.currency,
            "Paid Amount": item.paid,
            "Outstanding Balance": item.outstanding,
            "Ageing Category": item.aging_bucket,
            "Payment Status": item.status,
            "Narration": item.narration
        }));

        const worksheet = XLSX.utils.json_to_sheet(flatData);
        
        // Add styling to headers
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const address = XLSX.utils.encode_col(C) + "1"; // First row
            if (!worksheet[address]) continue;
            worksheet[address].s = {
                fill: {
                    fgColor: { rgb: "1c2b46" } // Dark Navy Blue
                },
                font: {
                    color: { rgb: "FFFFFF" }, // White
                    bold: true,
                    sz: 12
                },
                alignment: {
                    vertical: "center",
                    horizontal: "center"
                },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };
        }

        // Setting column widths for better readability
        const wscols = [
            { wch: 35 }, // Vendor Name
            { wch: 20 }, // Reference/Invoice #
            { wch: 15 }, // Expense Date
            { wch: 15 }, // Due Date
            { wch: 15 }, // Bill Amount
            { wch: 10 }, // Currency
            { wch: 15 }, // Paid Amount
            { wch: 20 }, // Outstanding Balance
            { wch: 20 }, // Ageing Category
            { wch: 15 }, // Payment Status
            { wch: 25 }  // Expense Type
        ];
        worksheet["!cols"] = wscols;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Payables Aging");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(dataBlob, `Vendor_Aging_Report_${moment().format("YYYY-MM-DD")}.xlsx`);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-AE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    {/* Header Section */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="page-title-box d-flex align-items-center justify-content-between">
                                <div>
                                    <h4 className="mb-0 font-size-18 fw-bold text-primary">Payable Aging Report</h4>
                                    <p className="text-muted mb-0 font-size-13 mt-1">Track and manage vendor liabilities across aging periods</p>
                                </div>
                                <div className="d-flex gap-2">
                                    <button
                                        onClick={exportToExcel}
                                        className="btn btn-primary d-flex align-items-center gap-2 shadow-sm"
                                        disabled={filteredData.length === 0}
                                    >
                                        <i className="mdi mdi-file-excel-outline"></i> Export Excel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filters Section */}
                    <div className="card mb-4 shadow-sm border-0">
                        <div className="card-body p-3">
                            <div className="row align-items-center g-3">
                                <div className="col-lg-3 col-md-4">
                                    <label className="form-label font-size-12 text-uppercase fw-bold text-muted mb-1">Live Search</label>
                                    <div className="input-group input-group-sm">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="mdi mdi-magnify text-primary"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0 bg-light"
                                            placeholder="Search results..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-2 col-md-4">
                                    <label className="form-label font-size-12 text-uppercase fw-bold text-muted mb-1">Vendor</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={selectedVendor}
                                        onChange={(e) => setSelectedVendor(e.target.value)}
                                    >
                                        <option value="">All Vendors</option>
                                        {vendors.map((vendor) => (
                                            <option key={vendor.id} value={vendor.id}>
                                                {vendor.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <div className="col-lg-2 col-md-3">
                                        <label className="form-label font-size-12 text-uppercase fw-bold text-muted mb-1">From Date</label>
                                        <DatePicker
                                            value={fromDate}
                                            onChange={(newValue) => setFromDate(newValue)}
                                            format="DD-MM-YYYY"
                                            slotProps={{
                                                textField: {
                                                    size: "small",
                                                    fullWidth: true,
                                                    variant: "outlined",
                                                },
                                            }}
                                        />
                                    </div>
                                    <div className="col-lg-2 col-md-3">
                                        <label className="form-label font-size-12 text-uppercase fw-bold text-muted mb-1">To Date</label>
                                        <DatePicker
                                            value={toDate}
                                            onChange={(newValue) => setToDate(newValue)}
                                            format="DD-MM-YYYY"
                                            slotProps={{
                                                textField: {
                                                    size: "small",
                                                    fullWidth: true,
                                                    variant: "outlined",
                                                },
                                            }}
                                        />
                                    </div>
                                </LocalizationProvider>

                                <div className="col-lg-3 col-md-6 d-flex align-self-end gap-2">
                                    <button 
                                        className="btn btn-success btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-2 fw-bold"
                                        onClick={handleApplyFilters}
                                        style={{ height: "38px" }}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        ) : (
                                            <i className="mdi mdi-filter-check"></i>
                                        )}
                                        Apply Filters
                                    </button>
                                    <button 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => {
                                            setFromDate(null);
                                            setToDate(null);
                                            setSelectedVendor("");
                                            setSearchTerm("");
                                            setData([]);
                                            setIsApplied(false);
                                        }}
                                        style={{ height: "38px", minWidth: "80px" }}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Summary Widgets */}
                    <div className="row mb-4">
                        <div className="col-xl-3 col-md-6">
                            <div className="card shadow-sm h-100 border-start border-primary border-3">
                                <div className="card-body">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="avatar-xs me-3">
                                            <span className="avatar-title rounded-circle bg-soft-primary text-primary">
                                                <i className="mdi mdi-currency-usd font-size-18"></i>
                                            </span>
                                        </div>
                                        <h5 className="font-size-14 mb-0 text-muted">Total Outstanding (AED)</h5>
                                    </div>
                                    <h3 className="mb-0 fw-bold text-primary">{formatCurrency(summaryData.totalOutstanding)}</h3>
                                    <p className="text-muted mb-0 mt-2 font-size-11 italic">Base currency equivalent (AED)</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-9 col-md-6">
                            <div className="card shadow-sm h-100">
                                <div className="card-body py-3">
                                    <h5 className="font-size-14 mb-3 border-bottom pb-2 text-muted fw-bold text-uppercase">Aging Summary (AED)</h5>
                                    <div className="row text-center">
                                        <div className="col-3 border-end">
                                            <p className="text-muted font-size-12 mb-1">0-30 Days</p>
                                            <h5 className="text-info fw-bold mb-0">{formatCurrency(summaryData.agingBuckets["0-30"])}</h5>
                                        </div>
                                        <div className="col-3 border-end">
                                            <p className="text-muted font-size-12 mb-1">31-60 Days</p>
                                            <h5 className="text-warning fw-bold mb-0">{formatCurrency(summaryData.agingBuckets["31-60"])}</h5>
                                        </div>
                                        <div className="col-3 border-end">
                                            <p className="text-muted font-size-12 mb-1">61-90 Days</p>
                                            <h5 className="text-orange fw-bold mb-0" style={{ color: "#fd7e14" }}>{formatCurrency(summaryData.agingBuckets["61-90"])}</h5>
                                        </div>
                                        <div className="col-3">
                                            <p className="text-muted font-size-12 mb-1">Over 90 Days</p>
                                            <h5 className="text-danger fw-bold mb-0">{formatCurrency(summaryData.agingBuckets["90+"])}</h5>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Data Table Section */}
                    <div className="row">
                        <div className="col-12">
                            <div className="card shadow-sm border-0">
                                <div className="card-body p-0">
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="mt-2 text-muted fw-medium font-size-14">Processing aging analysis...</p>
                                        </div>
                                    ) : !isApplied ? (
                                        <div className="text-center py-5 bg-soft-light">
                                            <i className="mdi mdi-calendar-search display-3 text-muted opacity-50"></i>
                                            <h5 className="mt-3 text-muted">Apply Filters</h5>
                                            <p className="text-muted font-size-13">Select filters and click 'Apply Filters' to generate the report.</p>
                                        </div>
                                    ) : Object.keys(groupedData).length === 0 ? (
                                        <div className="text-center py-5">
                                            <i className="mdi mdi-alert-circle-outline display-4 text-muted"></i>
                                            <h5 className="mt-3">No Records Found</h5>
                                            <p className="text-muted">No outstanding payables match your selected date range and vendor.</p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-nowrap table-hover align-middle mb-0">
                                                <thead className="table-light">
                                                    <tr className="text-uppercase font-size-11 letter-spacing-1 fw-bold">
                                                        <th className="ps-4 py-3">Invoice Details</th>
                                                        <th>Bill Date</th>
                                                        <th>Due Date (30d)</th>
                                                        <th className="text-end">Total Amount</th>
                                                        <th className="text-end">Paid Amount</th>
                                                        <th className="text-end">Outstanding</th>
                                                        <th className="text-center">Aging</th>
                                                        <th className="text-center pe-4">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {Object.entries(groupedData).map(([vendor, items]) => (
                                                        <React.Fragment key={vendor}>
                                                            {/* Vendor Header */}
                                                            <tr>
                                                                <td colSpan="8" className="bg-soft-light py-2 ps-4 border-top border-bottom border-light">
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <div className="d-flex align-items-center">
                                                                            <span className="avatar-xs me-2 d-none d-md-inline-block">
                                                                                <span className="avatar-title rounded-circle bg-primary-subtle text-primary font-size-12 fw-bold">
                                                                                    {vendor.charAt(0)}
                                                                                </span>
                                                                            </span>
                                                                            <span className="fw-bold text-dark font-size-13">{vendor}</span>
                                                                        </div>
                                                                        <div className="pe-4 text-end">
                                                                            <span className="text-muted font-size-11 text-uppercase me-2 fw-medium">Group Balance:</span>
                                                                            <span className="badge bg-white border text-primary font-size-12 px-3 py-1 shadow-sm">
                                                                                {formatCurrency(items.reduce((sum, item) => sum + parseFloat(item.outstanding.replace(/,/g, "")), 0))} 
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            {/* Invoice Rows */}
                                                            {items.map((item, index) => (
                                                                <tr key={`${vendor}-${index}`} className="border-bottom border-light">
                                                                    <td className="ps-4">
                                                                        <div>
                                                                            <h5 className="font-size-13 mb-1 text-dark fw-bold">{item.invoice_no}</h5>
                                                                            <span className="text-muted font-size-11">{item.narration}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td>{moment(item.date).format("DD MMM YYYY")}</td>
                                                                    <td>
                                                                        <span className="text-dark fw-medium">
                                                                            {moment(item.due_date).format("DD MMM YYYY")}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-end font-size-13">
                                                                        <div className="fw-medium text-dark">{formatCurrency(parseFloat(item.amount.replace(/,/g, "")))}</div>
                                                                        <div className="text-muted font-size-10 fw-bold">{item.currency}</div>
                                                                    </td>
                                                                    <td className="text-end font-size-13">
                                                                        <div className="text-success">{parseFloat(item.paid.replace(/,/g, "")) > 0 ? "+" + formatCurrency(parseFloat(item.paid.replace(/,/g, ""))) : "0.00"}</div>
                                                                        <div className="text-muted font-size-10">{item.currency}</div>
                                                                    </td>
                                                                    <td className="text-end font-size-13 fw-bold">
                                                                        <div className="text-danger">{formatCurrency(parseFloat(item.outstanding.replace(/,/g, "")))}</div>
                                                                        <div className="text-muted font-size-10">{item.currency}</div>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span 
                                                                            className={`badge rounded-pill font-size-11 px-3 py-1 ${
                                                                                item.aging_bucket === "0–30 Days" ? "bg-soft-info text-info border border-info" : 
                                                                                item.aging_bucket === "31–60 Days" ? "bg-soft-warning text-warning border border-warning" : 
                                                                                item.aging_bucket === "61–90 Days" ? "bg-soft-orange text-orange border border-warning" : 
                                                                                "bg-soft-danger text-danger border border-danger"
                                                                            }`}
                                                                        >
                                                                            {item.aging_bucket}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-center pe-4">
                                                                        <span className={`badge ${
                                                                            item.status === "Paid" ? "bg-success" : 
                                                                            item.status === "Partial" ? "bg-warning" : 
                                                                            "bg-danger"
                                                                        }`}>
                                                                            {item.status}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </React.Fragment>
                                                    ))}
                                                </tbody>
                                                {/* Total Footer */}
                                                <tfoot className="bg-light border-top border-2">
                                                    <tr>
                                                        <td colSpan="5" className="ps-4 py-3 fw-bold font-size-14 text-dark uppercase letter-spacing-1">Grand Total Payables</td>
                                                        <td className="text-end py-3 fw-bold font-size-18 text-primary shadow-sm" style={{ background: "#f8f9fa" }}>
                                                            {formatCurrency(summaryData.totalOutstanding)} 
                                                        </td>
                                                        <td colSpan="2"></td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PayableAgingReport;
